import { useState, useEffect, useCallback, useRef } from "react";
import config from "@/config";
import type { DashboardStatsQuery, VapiAssistantStats } from "@/types";
import { buildStatsQueryParams } from "@/lib/dashboard-api";

type Opts = {
  refetchKey?: number;
  /** When false, do not fetch (e.g. parent supplies bundle payload). */
  enabled?: boolean;
};

export function useVapiAssistantStats(
  caseFilters: DashboardStatsQuery,
  options?: Opts,
) {
  const refetchKey = options?.refetchKey ?? 0;
  const enabled = options?.enabled ?? true;
  const [data, setData] = useState<VapiAssistantStats | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);

  const fetchSeqRef = useRef(0);

  const fetchData = useCallback(async () => {
    const seq = ++fetchSeqRef.current;
    setLoading(true);
    setError(null);
    try {
      const params = buildStatsQueryParams(caseFilters);
      const qs = params.toString();
      const res = await fetch(
        `${config.API_BASE_URL}/api/dashboard/vapi-assistant-stats${qs ? `?${qs}` : ""}`,
      );
      if (!res.ok) {
        const t = await res.text();
        throw new Error(t || `Request failed: ${res.status}`);
      }
      const json = (await res.json()) as VapiAssistantStats;
      if (seq !== fetchSeqRef.current) return;
      setData(json);
    } catch (e) {
      if (seq !== fetchSeqRef.current) return;
      setError(
        e instanceof Error ? e.message : "Failed to load VAPI assistant stats",
      );
    } finally {
      if (seq === fetchSeqRef.current) {
        setLoading(false);
      }
    }
  }, [caseFilters]);

  useEffect(() => {
    if (!enabled) {
      fetchSeqRef.current += 1;
      setLoading(false);
      return;
    }
    void fetchData();
  }, [enabled, fetchData, refetchKey]);

  return { data, loading, error, refresh: fetchData };
}
